"use client"

import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { z } from "zod"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form"
import { DatePicker } from "@/components/ui/date-picker"
import { useLanguage } from "@/contexts/language-context"
import { type WorkerFormData as Worker } from "@/lib/schemas"

// Schema for the date form
const workerDateSchema = z.object({
  date: z.date({
    required_error: "Date is required",
  }),
})

type WorkerDateFormData = z.infer<typeof workerDateSchema>

// Today at midnight, as the date picker gives every date it hands back. The worker schema compares whole
// timestamps against the engagement dates, so today with the time of day still on it reads as later than
// an engagement ending today.
const today = () => {
  const now = new Date()
  return new Date(now.getFullYear(), now.getMonth(), now.getDate())
}

export interface WorkerRemovalDateDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  worker?: Worker
  onConfirm: (date: Date) => void
  engagementStartDate?: Date | null
  engagementEndDate?: Date | null
}

export const WorkerRemovalDateDialog = ({
  open,
  onOpenChange,
  worker,
  onConfirm,
  engagementStartDate,
  engagementEndDate,
}: WorkerRemovalDateDialogProps) => {
  const { dictionary } = useLanguage()

  // Calculate date boundaries for last day of work
  // Minimum date: prioritize worker's actual start date, fallback to engagement start
  const minDate = worker?.firstDayOfWork || engagementStartDate || undefined
  
  // Maximum date: engagement end date (convert null to undefined for DatePicker)
  const maxDate = engagementEndDate ?? undefined

  const form = useForm<WorkerDateFormData>({
    resolver: zodResolver(workerDateSchema),
    mode: 'onChange',
    defaultValues: {
      date: today(),
    }
  })

  const handleSubmit = () => {
    const formData = form.getValues()
    if (formData.date) {
      onConfirm(formData.date)
      onOpenChange(false)
      form.reset({ date: today() })
    }
  }

  const handleCancel = () => {
    onOpenChange(false)
    form.reset({ date: today() })
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>
            {dictionary?.engagement?.workers?.setLastDay || "Set Last Day of Work"}
          </DialogTitle>
          <DialogDescription>
            {dictionary?.engagement?.workers?.setLastDayDescription || "When will this worker stop working on the engagement?"}
          </DialogDescription>
        </DialogHeader>
        
        <Form {...form}>
          <div className="space-y-4 py-4">
            <FormField
              control={form.control}
              name="date"
              render={({ field }) => (
                <FormItem className="space-y-2.5">
                  <FormLabel className="text-sm font-medium">
                    {dictionary?.engagement?.workers?.lastDayOfWork || "Last Day of Work"}
                  </FormLabel>
                  <FormControl>
                    <DatePicker
                      {...field}
                      placeholder={dictionary?.engagement?.core?.pickDate || "Pick a date"}
                      minDate={minDate}
                      maxDate={maxDate}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </Form>
        
        <DialogFooter>
          <Button variant="outline" onClick={handleCancel}>
            {dictionary?.engagement?.core?.cancel || "Cancel"}
          </Button>
          <Button 
            onClick={handleSubmit}
            disabled={!form.formState.isValid}
          >
            {dictionary?.engagement?.workers?.removeWorker || "Remove Worker"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
} 